import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { HelpPanel } from '../components/ui/help-panel';
import {
  Users,
  Shield,
  Settings,
  UserPlus,
  Trash2,
  AlertTriangle,
  Crown,
  ShieldCheck,
  Calculator,
  BookOpen,
  Eye,
  CreditCard,
  HelpCircle,
  MoreHorizontal,
  Check,
  Search,
  Building2,
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Database,
  Server,
  Zap,
  BarChart3,
  RefreshCw,
  UserCog,
  LogIn,
  LogOut,
  Flag,
  History,
  FileText,
  Sparkles,
  ChevronRight
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import AccessOverridesPanel from '../components/AccessOverridesPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role configurations
const ROLES = [
  { value: 'owner', label: 'Owner', icon: Crown, color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
  { value: 'admin', label: 'Admin', icon: ShieldCheck, color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  { value: 'accountant', label: 'Accountant', icon: Calculator, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  { value: 'bookkeeper', label: 'Bookkeeper', icon: BookOpen, color: 'text-emerald-600', bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' },
  { value: 'read_only', label: 'Read Only', icon: Eye, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-900/30' },
  { value: 'client_portal', label: 'Client Portal', icon: CreditCard, color: 'text-pink-600', bgColor: 'bg-pink-100 dark:bg-pink-900/30' },
];

// System Health Card Component
const HealthCard = ({ icon: Icon, title, value, status, subtext, color }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center",
          color === 'emerald' && "bg-emerald-500/10",
          color === 'blue' && "bg-blue-500/10",
          color === 'amber' && "bg-amber-500/10",
          color === 'rose' && "bg-rose-500/10",
          color === 'violet' && "bg-violet-500/10"
        )}>
          <Icon className={cn(
            "w-5 h-5",
            color === 'emerald' && "text-emerald-500",
            color === 'blue' && "text-blue-500",
            color === 'amber' && "text-amber-500",
            color === 'rose' && "text-rose-500",
            color === 'violet' && "text-violet-500"
          )} />
        </div>
        {status && (
          <Badge className={cn(
            "text-[10px]",
            status === 'healthy' && "bg-emerald-500/10 text-emerald-600",
            status === 'warning' && "bg-amber-500/10 text-amber-600",
            status === 'error' && "bg-rose-500/10 text-rose-600"
          )}>
            {status === 'healthy' && <CheckCircle2 className="w-3 h-3 mr-1" />}
            {status === 'warning' && <AlertCircle className="w-3 h-3 mr-1" />}
            {status === 'error' && <AlertTriangle className="w-3 h-3 mr-1" />}
            {status}
          </Badge>
        )}
      </div>
      <div className="mt-3">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
      </div>
    </CardContent>
  </Card>
);

// User Search Result Card
const UserSearchCard = ({ user, onViewDetails, onImpersonate }) => (
  <Card className="hover:border-primary/30 transition-colors">
    <CardContent className="p-4">
      <div className="flex items-center gap-4">
        <Avatar className="w-12 h-12">
          <AvatarImage src={user.picture} />
          <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-500 text-white">
            {user.name?.charAt(0)?.toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{user.name}</p>
          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-[10px]">
              <Building2 className="w-3 h-3 mr-1" />
              {user.workspace_count || 0} workspaces
            </Badge>
            {user.last_login && (
              <span className="text-xs text-muted-foreground">
                Last login: {formatDistanceToNow(new Date(user.last_login), { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onViewDetails(user)}>
            <Eye className="w-4 h-4 mr-1" />
            Details
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => onImpersonate(user)}
            className="text-amber-600 border-amber-500/30 hover:bg-amber-500/10"
          >
            <LogIn className="w-4 h-4 mr-1" />
            Impersonate
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Feature Flag Row
const FeatureFlagRow = ({ flag, onToggle }) => (
  <TableRow>
    <TableCell>
      <div>
        <p className="font-medium">{flag.name}</p>
        <p className="text-sm text-muted-foreground">{flag.description}</p>
      </div>
    </TableCell>
    <TableCell>
      <Badge variant="secondary" className="font-mono text-xs">
        {flag.key}
      </Badge>
    </TableCell>
    <TableCell>
      <div className="flex items-center gap-2">
        <Progress value={flag.rollout_percentage} className="w-20 h-2" />
        <span className="text-sm font-mono">{flag.rollout_percentage}%</span>
      </div>
    </TableCell>
    <TableCell>
      <Switch 
        checked={flag.enabled} 
        onCheckedChange={() => onToggle(flag.key)}
      />
    </TableCell>
  </TableRow>
);

export default function AdminPanel({ embedded = false }) {
  const { currentWorkspace, userRole } = useWorkspace();
  const { user: currentUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // System health data
  const [systemHealth, setSystemHealth] = useState({
    activeUsers: 0,
    totalWorkspaces: 0,
    bankConnections: { healthy: 0, errored: 0 },
    ocrJobsPending: 0,
    aiSuggestionsPending: 0
  });
  
  // Team members
  const [members, setMembers] = useState([]);
  
  // User search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Impersonation
  const [impersonating, setImpersonating] = useState(null);
  const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(null);
  
  // Feature flags
  const [featureFlags, setFeatureFlags] = useState([
    { key: 'ai_buck_v2', name: 'AI Buck V2', description: 'New AI categorization engine', rollout_percentage: 100, enabled: true },
    { key: 'reconcile_buck', name: 'Smart Reconcile', description: 'AI-assisted statement reconciliation', rollout_percentage: 100, enabled: true },
    { key: 'premium_dashboard', name: 'Premium Dashboard', description: 'Advanced dashboard widgets', rollout_percentage: 100, enabled: true },
    { key: 'multi_company_view', name: 'Multi-Company View', description: 'Consolidated multi-workspace view', rollout_percentage: 100, enabled: true },
  ]);
  
  // Role change state
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [changingRole, setChangingRole] = useState(false);
  
  // Remove member state
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  
  // Invite member state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('read_only');
  const [inviting, setInviting] = useState(false);

  // Fetch system health
  const fetchSystemHealth = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      // Fetch various stats to build system health
      const [membersRes, bankRes, aiRes] = await Promise.all([
        fetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members`),
        fetch(`${API_URL}/api/bank-accounts?workspace_id=${currentWorkspace.workspace_id}`),
        fetch(`${API_URL}/api/ai-buck/suggestions?workspace_id=${currentWorkspace.workspace_id}&status=pending&limit=1`)
      ]);
      
      const members = membersRes.ok ? await membersRes.json() : [];
      const banks = bankRes.ok ? await bankRes.json() : [];
      const aiData = aiRes.ok ? await aiRes.json() : { total: 0 };
      
      setSystemHealth({
        activeUsers: members.length,
        totalWorkspaces: 1, // Current workspace
        bankConnections: {
          healthy: banks.filter(b => b.status === 'active').length,
          errored: banks.filter(b => b.status === 'error').length
        },
        ocrJobsPending: 0,
        aiSuggestionsPending: aiData.total || 0
      });
      setMembers(members);
    } catch (error) {
      console.error('Failed to fetch system health:', error);
    }
  }, [currentWorkspace]);

  // Fetch members
  const fetchMembers = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members`
      );
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchSystemHealth(), fetchMembers()]);
      setLoading(false);
    };
    load();
  }, [fetchSystemHealth, fetchMembers]);

  // Search users
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      // Note: This would need a backend endpoint for global user search
      // For now, we'll search within workspace members
      const filtered = members.filter(m => 
        m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  // Handle impersonation
  const handleImpersonate = (user) => {
    setShowImpersonateConfirm(user);
  };

  const confirmImpersonate = async () => {
    if (!showImpersonateConfirm) return;
    
    setImpersonating(showImpersonateConfirm);
    toast.success(`Now viewing as ${showImpersonateConfirm.name}`, {
      description: 'Click "Exit Impersonation" to return to your account'
    });
    setShowImpersonateConfirm(null);
  };

  const exitImpersonation = () => {
    setImpersonating(null);
    toast.success('Exited impersonation mode');
  };

  // Handle role change
  const handleRoleChange = async () => {
    if (!pendingRoleChange) return;
    
    setChangingRole(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members/${pendingRoleChange.userId}/role`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: pendingRoleChange.newRole }),
        }
      );
      
      if (response.ok) {
        toast.success(`Role updated to ${pendingRoleChange.newRole}`);
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setChangingRole(false);
      setPendingRoleChange(null);
    }
  };

  // Handle member removal
  const handleRemoveMember = async () => {
    if (!pendingRemoval) return;
    
    setRemoving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members/${pendingRemoval.userId}`,
        {
          method: 'DELETE',
          }
      );
      
      if (response.ok) {
        toast.success('Member removed');
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove member');
      }
    } catch (error) {
      toast.error('Failed to remove member');
    } finally {
      setRemoving(false);
      setPendingRemoval(null);
    }
  };

  // Handle invite
  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/members`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
        }
      );
      
      if (response.ok) {
        toast.success(`Invitation sent to ${inviteEmail}`);
        setShowInviteDialog(false);
        setInviteEmail('');
        setInviteRole('read_only');
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  // Toggle feature flag
  const toggleFeatureFlag = (flagKey) => {
    setFeatureFlags(prev => prev.map(flag => 
      flag.key === flagKey ? { ...flag, enabled: !flag.enabled } : flag
    ));
    toast.success('Feature flag updated');
  };

  // Check if current user can manage admin features
  const canManageAdmin = ['owner', 'admin'].includes(userRole);

  if (!canManageAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              You need Owner or Admin permissions to access the Admin Panel.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <div>
              <p className="font-medium text-amber-700 dark:text-amber-400">
                Impersonation Mode Active
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-500">
                Viewing as {impersonating.name} ({impersonating.email})
              </p>
            </div>
          </div>
          <Button 
            onClick={exitImpersonation}
            variant="outline"
            className="border-amber-500/30 text-amber-700 hover:bg-amber-500/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Exit Impersonation
          </Button>
        </div>
      )}

      {/* Header — hidden when rendered inside Settings (the Settings page
          already shows a page header). Standalone /owner-admin route
          continues to show it for direct-link users. */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Admin Panel</h1>
            <p className="text-muted-foreground">System management and user administration</p>
          </div>
          <Button onClick={() => setShowInviteDialog(true)} data-testid="invite-member-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end">
          <Button onClick={() => setShowInviteDialog(true)} data-testid="invite-member-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-5">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Activity className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access">
            <Shield className="w-4 h-4 mr-2" />
            Access
          </TabsTrigger>
          <TabsTrigger value="search" data-testid="tab-search">
            <Search className="w-4 h-4 mr-2" />
            Search
          </TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags">
            <Flag className="w-4 h-4 mr-2" />
            Features
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab - System Health */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <HealthCard
              icon={Users}
              title="Active Users"
              value={systemHealth.activeUsers}
              color="blue"
              status="healthy"
            />
            <HealthCard
              icon={Building2}
              title="Workspaces"
              value={systemHealth.totalWorkspaces}
              color="emerald"
            />
            <HealthCard
              icon={CreditCard}
              title="Bank Connections"
              value={`${systemHealth.bankConnections.healthy}/${systemHealth.bankConnections.healthy + systemHealth.bankConnections.errored}`}
              color={systemHealth.bankConnections.errored > 0 ? 'amber' : 'emerald'}
              status={systemHealth.bankConnections.errored > 0 ? 'warning' : 'healthy'}
              subtext={systemHealth.bankConnections.errored > 0 ? `${systemHealth.bankConnections.errored} errored` : 'All connected'}
            />
            <HealthCard
              icon={Sparkles}
              title="AI Suggestions"
              value={systemHealth.aiSuggestionsPending}
              color="violet"
              subtext="Pending review"
            />
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => setActiveTab('search')}>
                  <Search className="w-4 h-4 mr-2" />
                  Search Users
                </Button>
                <Button variant="outline" onClick={() => setActiveTab('users')}>
                  <UserCog className="w-4 h-4 mr-2" />
                  Manage Team
                </Button>
                <Button variant="outline" onClick={() => setActiveTab('flags')}>
                  <Flag className="w-4 h-4 mr-2" />
                  Feature Flags
                </Button>
                <Button variant="outline" onClick={fetchSystemHealth}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Stats
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* System Status card removed per Feb 30 2026 user feedback —
              end users (and even workspace owners) don't need to see
              backend infra status. The Emergent Support team can pull
              this from internal dashboards if needed. */}
        </TabsContent>

        {/* Users Tab - Team Management */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>Manage roles and permissions for workspace members</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : members.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => {
                      const roleConfig = ROLES.find(r => r.value === member.role) || ROLES[4];
                      const RoleIcon = roleConfig.icon;
                      const isCurrentUser = member.user_id === currentUser?.user_id;
                      const canModify = !isCurrentUser && (userRole === 'owner' || (userRole === 'admin' && member.role !== 'owner' && member.role !== 'admin'));
                      
                      return (
                        <TableRow key={member.user_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={member.picture} />
                                <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-500 text-white">
                                  {member.name?.charAt(0)?.toUpperCase() || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{member.name}</p>
                                <p className="text-sm text-muted-foreground">{member.email}</p>
                              </div>
                              {isCurrentUser && (
                                <Badge variant="secondary" className="text-xs">You</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className={cn("inline-flex items-center gap-2 px-2 py-1 rounded-md", roleConfig.bgColor)}>
                              <RoleIcon className={cn("w-4 h-4", roleConfig.color)} />
                              <span className={cn("text-sm font-medium", roleConfig.color)}>
                                {roleConfig.label}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {member.joined_at ? format(new Date(member.joined_at), 'MMM d, yyyy') : '-'}
                          </TableCell>
                          <TableCell>
                            {canModify && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem 
                                    onClick={() => setPendingRoleChange({ userId: member.user_id, currentRole: member.role, newRole: null, name: member.name })}
                                  >
                                    <UserCog className="w-4 h-4 mr-2" />
                                    Change Role
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => setPendingRemoval({ userId: member.user_id, name: member.name })}
                                    className="text-rose-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Remove
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No team members yet</p>
                  <Button className="mt-4" onClick={() => setShowInviteDialog(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite First Member
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Overrides Tab */}
        <TabsContent value="access" className="space-y-6">
          <AccessOverridesPanel />
        </TabsContent>

        {/* Search Tab */}
        <TabsContent value="search" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>User Search</CardTitle>
              <CardDescription>Search for users by email, name, or workspace ID</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                <Input
                  placeholder="Search by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="max-w-md"
                  data-testid="user-search-input"
                />
                <Button onClick={handleSearch} disabled={searching}>
                  <Search className="w-4 h-4 mr-2" />
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </div>

              {searchResults.length > 0 ? (
                <div className="space-y-3">
                  {searchResults.map(user => (
                    <UserSearchCard
                      key={user.user_id}
                      user={user}
                      onViewDetails={(u) => setSelectedUser(u)}
                      onImpersonate={handleImpersonate}
                    />
                  ))}
                </div>
              ) : searchQuery && !searching ? (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No users found</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Enter a search query to find users</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Flags Tab */}
        <TabsContent value="flags" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Feature Flags</CardTitle>
              <CardDescription>
                Gradually roll out new functionality without redeploying. Toggle a flag off to instantly
                hide a feature from this workspace — useful for staged rollouts, beta testing, or rolling back a
                problematic release. Most users never touch these.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Rollout</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {featureFlags.map(flag => (
                    <FeatureFlagRow
                      key={flag.key}
                      flag={flag}
                      onToggle={toggleFeatureFlag}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join this workspace
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.filter(r => r.value !== 'owner').map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon className="w-4 h-4" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviting}>
              {inviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonation Confirmation Dialog */}
      <AlertDialog open={!!showImpersonateConfirm} onOpenChange={() => setShowImpersonateConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Impersonation
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to view the application as <strong>{showImpersonateConfirm?.name}</strong>. 
              This will allow you to see exactly what this user sees. All actions will be logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmImpersonate}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Start Impersonation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Role Change Dialog */}
      {pendingRoleChange && (
        <Dialog open={!!pendingRoleChange} onOpenChange={() => setPendingRoleChange(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Role for {pendingRoleChange.name}</DialogTitle>
              <DialogDescription>
                Select a new role for this team member
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Select 
                value={pendingRoleChange.newRole || ''} 
                onValueChange={(value) => setPendingRoleChange({...pendingRoleChange, newRole: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select new role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.filter(r => r.value !== 'owner').map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon className="w-4 h-4" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingRoleChange(null)}>
                Cancel
              </Button>
              <Button 
                onClick={handleRoleChange} 
                disabled={!pendingRoleChange.newRole || changingRole}
              >
                {changingRole ? 'Updating...' : 'Update Role'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Remove Member Confirmation */}
      <AlertDialog open={!!pendingRemoval} onOpenChange={() => setPendingRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{pendingRemoval?.name}</strong> from this workspace? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveMember}
              className="bg-rose-500 hover:bg-rose-600"
              disabled={removing}
            >
              {removing ? 'Removing...' : 'Remove Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
